if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (!customElements.get('predictive-search')) {
  try {
    customElements.define(
      'predictive-search',
      class PredictiveSearch extends HTMLFormElement {
      constructor() {
        super();

        this.cachedMap = new Map();
        this.requestVersion = 0;
        this.focusElement = this.input;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.onInputClick.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));
        this.input.addEventListener('focus', this.onFocus.bind(this));
        this.input.addEventListener('keydown', this.onKeyDown.bind(this));
        this.dataset.predictiveEnhanced = 'true';
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      get typewriter() {
        return this.input.previousElementSibling;
      }

      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onInputClick() {
        this.hideTypewriter();

        if (
          this.closest('.collection')
          && this.getQuery().length > 0
          && !document.body.classList.contains('predictive-search-open')
        ) this.onFocus();
      }

      onFocus() {
        if (this.closest('.collection')) {
          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) {
            this.removeAttribute('results');
            this.input.setAttribute('aria-expanded', 'false');
            document.body.classList.remove('predictive-search-open');
            return;
          }

          document.body.classList.add('predictive-search-open');

          const url = this.buildUrl().toString();
          this.renderSection(url);
        }
      }

      onKeyDown(event) {
        if (
          event.key !== 'Escape'
          || !this.closest('.collection')
          || !document.body.classList.contains('predictive-search-open')
        ) return;

        event.preventDefault();
        this.closeResults();
      }

      closeResults() {
        this.requestVersion += 1;
        this.abortController?.abort();
        this.abortController = null;
        this.removeAttribute('loading');
        this.removeAttribute('results');
        this.input.setAttribute('aria-expanded', 'false');
        this.resultsContainer?.setAttribute('aria-busy', 'false');
        document.body.classList.remove('predictive-search-open');
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.closeResults();
        this.input.value = '';
        this.input.focus();
        this.resultsContainer?.replaceChildren();
      }

      getQuery() {
        return this.input.value.trim();
      }

      get resultsContainer() {
        return document.getElementById('PredictiveSearchResults-' + theme.utils.sectionId(this));
      }

      buildUrl() {
        const url = new URL(theme.routes.predictive_search_url, window.location.origin);

        url.searchParams.set('q', this.getQuery());
        url.searchParams.set('resources[limit]', this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 3);
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('section_id', theme.utils.sectionId(this));
        return url;
      }

      onChange() {
        if (this.getQuery().length === 0) {
          this.clear();
          return;
        }

        this.hideTypewriter();
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');
        }

        const url = this.buildUrl().toString();
        this.renderSection(url);
      }

      renderSection(url) {
        const requestVersion = ++this.requestVersion;
        this.abortController?.abort();
        this.abortController = null;
        this.removeAttribute('loading');
        this.removeAttribute('results');
        this.input.setAttribute('aria-expanded', 'false');
        this.resultsContainer?.setAttribute('aria-busy', 'false');

        this.cachedMap.has(url)
          ? this.renderSectionFromCache(url, requestVersion)
          : this.renderSectionFromFetch(url, requestVersion);
      }

      renderSectionFromCache(url, requestVersion) {
        if (requestVersion !== this.requestVersion || this.getQuery().length === 0) return;

        const responseText = this.cachedMap.get(url);
        if (!this.renderSearchResults(responseText)) {
          this.cachedMap.delete(url);
          document.body.classList.remove('predictive-search-open');
          return;
        }

        this.setAttribute('results', '');
        this.input.setAttribute('aria-expanded', 'true');
      }

      renderSectionFromFetch(url, requestVersion) {
        this.abortController = new AbortController();

        this.setAttribute('loading', '');
        this.resultsContainer?.setAttribute('aria-busy', 'true');

        fetch(url, { signal: this.abortController.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (requestVersion !== this.requestVersion || this.getQuery().length === 0) return;

            if (!this.renderSearchResults(responseText)) {
              document.body.classList.remove('predictive-search-open');
              return;
            }

            this.cachedMap.set(url, responseText);

            this.setAttribute('results', '');
            this.input.setAttribute('aria-expanded', 'true');
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);

              if (requestVersion === this.requestVersion) {
                this.removeAttribute('results');
                this.input.setAttribute('aria-expanded', 'false');
                document.body.classList.remove('predictive-search-open');
              }
            }
          })
          .finally(() => {
            if (requestVersion === this.requestVersion) {
              this.removeAttribute('loading');
              this.resultsContainer?.setAttribute('aria-busy', 'false');
              this.abortController = null;
            }
          });
      }

      renderSearchResults(responseText) {
        const id = 'PredictiveSearchResults-' + theme.utils.sectionId(this);
        const currentResults = this.resultsContainer;
        if (currentResults === null) return false;

        const nextResults = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .getElementById(id);

        if (nextResults === null) return false;

        currentResults.innerHTML = nextResults.innerHTML;
        return true;
      }
      }, { extends: 'form' }
    );
  }
  catch (error) {
    console.warn('Customized predictive search is not available; using the compatible controller.', error);
  }
}

/*
 * Some browsers and embedded webviews do not upgrade customized built-in
 * forms (`<form is="predictive-search">`). Keep the native form semantics and
 * progressively attach the same behaviour when that upgrade is unavailable.
 */
const initPredictiveSearchFallback = () => {
  document.querySelectorAll('form[is="predictive-search"]').forEach((form) => {
    if (form.dataset.predictiveEnhanced === 'true' || form.dataset.predictiveFallback === 'true') return;

    const input = form.querySelector('input[type="search"]');
    const resetButton = form.querySelector('button[type="reset"]');
    const sectionId = form.dataset.sectionId;
    const resultsContainer = document.getElementById(`PredictiveSearchResults-${sectionId}`);

    if (!input || !resetButton || !sectionId || !resultsContainer) return;

    form.dataset.predictiveFallback = 'true';

    let abortController = null;
    let debounceTimer = null;
    let requestVersion = 0;

    const closeResults = () => {
      requestVersion += 1;
      abortController?.abort();
      abortController = null;
      form.removeAttribute('loading');
      form.removeAttribute('results');
      input.setAttribute('aria-expanded', 'false');
      resultsContainer.setAttribute('aria-busy', 'false');
      document.body.classList.remove('predictive-search-open');
    };

    const clear = (event = null) => {
      event?.preventDefault();
      window.clearTimeout(debounceTimer);
      closeResults();
      input.value = '';
      resultsContainer.replaceChildren();
      input.focus();
    };

    const renderResults = () => {
      const query = input.value.trim();
      if (query.length === 0) {
        clear();
        return;
      }

      const typewriter = input.previousElementSibling;
      if (typewriter && !typewriter.hasAttribute('hidden')) typewriter.setAttribute('hidden', '');

      const currentVersion = ++requestVersion;
      abortController?.abort();
      abortController = new AbortController();

      const url = new URL(theme.routes.predictive_search_url, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[limit]', form.dataset.limit ? parseInt(form.dataset.limit) : 3);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('section_id', sectionId);

      form.setAttribute('loading', '');
      form.removeAttribute('results');
      input.setAttribute('aria-expanded', 'false');
      resultsContainer.setAttribute('aria-busy', 'true');

      if (form.closest('.collection')) document.body.classList.add('predictive-search-open');

      fetch(url, { signal: abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
          return response.text();
        })
        .then((responseText) => {
          if (currentVersion !== requestVersion || input.value.trim().length === 0) return;

          const nextResults = new DOMParser()
            .parseFromString(responseText, 'text/html')
            .getElementById(`PredictiveSearchResults-${sectionId}`);

          if (!nextResults) throw new Error('Predictive search response did not include the expected result region.');

          resultsContainer.innerHTML = nextResults.innerHTML;
          form.setAttribute('results', '');
          input.setAttribute('aria-expanded', 'true');
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.error(error);
            if (currentVersion === requestVersion) closeResults();
          }
        })
        .finally(() => {
          if (currentVersion === requestVersion) {
            form.removeAttribute('loading');
            resultsContainer.setAttribute('aria-busy', 'false');
            abortController = null;
          }
        });
    };

    form.closeResults = closeResults;
    resetButton.addEventListener('click', clear);
    input.addEventListener('input', () => {
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(renderResults, 300);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length > 0 && !form.hasAttribute('results') && !form.hasAttribute('loading')) renderResults();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !form.hasAttribute('results')) return;
      event.preventDefault();
      closeResults();
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPredictiveSearchFallback, { once: true });
}
else {
  initPredictiveSearchFallback();
}

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        const searchForm = this.closest('form[is="predictive-search"]');

        setTimeout(() => {
          if (typeof searchForm?.closeResults === 'function') {
            searchForm.closeResults();
          }
          else {
            document.body.classList.remove('predictive-search-open');
            searchForm?.querySelector('input[type="search"]')?.setAttribute('aria-expanded', 'false');
          }
        });
      }
    }
  );
}
